import './AdditionalFilters.css';
import CloseFilled from 'assets/img/CloseFilled.svg';
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
  CircularProgress
} from '@material-ui/core';
import MultiSelectDropdown from 'views/components/careConnectPage/MultiSelectDropdown';
import MUIRangeSlider from './MUIRangeSlider';
import { useState, useEffect } from 'react';
import { AppliedFilters } from 'views/components/careConnectPage/ClientCare/ClientTabPage';
import { RECOMMENDED_HOURS_RANGE } from 'utils/constants/calendarFilters';

interface FilterOption {
  id?: string | number;
  title?: string;
  label?: string;
  value?: string;
}

interface FilterData {
  waitListStatus?: FilterOption[];
  waitListReasons?: FilterOption[];
  insuranceCategories?: FilterOption[];
  insurancePayors?: FilterOption[];
  insurancePlans?: FilterOption[];
}

interface AdditionalFiltersProps {
  show: boolean;
  close: () => void;
  onFiltersChange?: (filters: AppliedFilters) => void;
  filterData?: FilterData;
  isLoading?: boolean;
  setHasSearched?: (hasSearched: boolean) => void;
  appliedFilters?: AppliedFilters;
  disabledClearFilters?: boolean;
}

const AdditionalFilters: React.FC<AdditionalFiltersProps> = ({
  show,
  close,
  onFiltersChange,
  filterData = {},
  isLoading,
  setHasSearched,
  appliedFilters,
  disabledClearFilters
}) => {
  const [selectedFilters, setSelectedFilters] = useState<AppliedFilters>({});

  useEffect(() => {
    if (show && appliedFilters) {
      setSelectedFilters(appliedFilters);
    }
  }, [show, appliedFilters]);

  const transformOptions = (
    options: FilterOption[] = []
  ): { label: string; value: string }[] => {
    const transformed = options.map(option => ({
      label: option.title || option.label || '',
      value:
        option.id?.toString() ||
        option.value ||
        option.title ||
        option.label ||
        ''
    }));
    const values = transformed.map(opt => opt.value);
    const uniqueValues = new Set(values);
    if (values.length !== uniqueValues.size) {
      console.warn('Duplicate values found in options:', values);
    }
    return transformed;
  };

  const getSelectedValues = (
    selectedLabels: string[] = [],
    options: FilterOption[] = []
  ): string[] => {
    return selectedLabels
      .map(label => {
        const option = options.find(opt => opt.title === label);
        return option?.id?.toString() || option?.value || '';
      })
      .filter(Boolean);
  };

  const hasAdditionalFilters = () => {
    return (
      (selectedFilters.carePlanstatus &&
        selectedFilters.carePlanstatus.length > 0) ||
      (selectedFilters.isWaitlistReason &&
        selectedFilters.isWaitlistReason.length > 0) ||
      (selectedFilters.isJumpstarter !== null &&
        selectedFilters.isJumpstarter !== undefined) ||
      (selectedFilters.isVBC !== null && selectedFilters.isVBC !== undefined) ||
      (selectedFilters.checkedInsuranceCategories &&
        selectedFilters.checkedInsuranceCategories.length > 0) ||
      (selectedFilters.checkedInsurancePayors &&
        selectedFilters.checkedInsurancePayors.length > 0) ||
      (selectedFilters.checkedInsurancePlans &&
        selectedFilters.checkedInsurancePlans.length > 0) ||
      (selectedFilters.recommendedHoursRange &&
        (selectedFilters.recommendedHoursRange[0] !==
          RECOMMENDED_HOURS_RANGE[0] ||
          selectedFilters.recommendedHoursRange[1] !==
            RECOMMENDED_HOURS_RANGE[1]))
    );
  };
  const triggerFilterChange = (newFilters: AppliedFilters) => {
    onFiltersChange?.(newFilters);
    setHasSearched?.(true);
  };

  const handleCarePlanChange = (value: string[]) => {
    const uniqueValues = [...new Set(value)];
    const newFilters = {
      ...selectedFilters,
      carePlanstatus: uniqueValues
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleWaitlistReasonChange = (value: string[]) => {
    const uniqueValues = [...new Set(value)];
    const newFilters = {
      ...selectedFilters,
      isWaitlistReason: uniqueValues
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleJumpstarterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value =
      event.target.value === 'yes'
        ? true
        : event.target.value === 'no'
        ? false
        : null;
    const newFilters = {
      ...selectedFilters,
      isJumpstarter: value
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleVbcOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      event.target.value === 'yes'
        ? true
        : event.target.value === 'no'
        ? false
        : null;
    const newFilters = {
      ...selectedFilters,
      isVBC: value
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleInsuranceCategoryChange = (value: string[]) => {
    const labels = value
      .map(
        val =>
          filterData.insuranceCategories?.find(
            opt => opt.id?.toString() === val
          )?.title
      )
      .filter(Boolean) as string[];
    const newFilters = {
      ...selectedFilters,
      checkedInsuranceCategories: labels
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleInsurancePayerChange = (value: string[]) => {
    const labels = value
      .map(
        val =>
          filterData.insurancePayors?.find(opt => opt.id?.toString() === val)
            ?.title
      )
      .filter(Boolean) as string[];
    const newFilters = {
      ...selectedFilters,
      checkedInsurancePayors: labels
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleInsurancePlanChange = (value: string[]) => {
    const labels = value
      .map(
        val =>
          filterData.insurancePlans?.find(opt => opt.id?.toString() === val)
            ?.title
      )
      .filter(Boolean) as string[];
    const newFilters = {
      ...selectedFilters,
      checkedInsurancePlans: labels
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleRecommendedHoursChange = (value: [number, number]) => {
    const newFilters = {
      ...selectedFilters,
      recommendedHoursRange: value
    };
    setSelectedFilters(newFilters);
    triggerFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      ...selectedFilters,
      carePlanstatus: [],
      isWaitlistReason: [],
      isVBC: null,
      checkedInsuranceCategories: [],
      checkedInsurancePayors: [],
      checkedInsurancePlans: [],
      isJumpstarter: null,
      recommendedHoursRange: RECOMMENDED_HOURS_RANGE
    };
    setSelectedFilters(clearedFilters);
    triggerFilterChange(clearedFilters);
  };

  return (
    <>
      {show ? (
        <div
          className="additional-filters-panel open"
          id="additional-filters-panel"
        >
          <div className="panel-header">
            <div className="panel-body-header">
              <Typography className="filters-title">
                Additional Filters
              </Typography>
            </div>
            <span className="panel-cancel" onClick={close}>
              <img
                style={{ cursor: 'pointer' }}
                alt="closeIcon"
                src={CloseFilled}
              />
            </span>
          </div>
          <Typography variant="subtitle1" className="filter-category-subtitle">
            To refine search, apply filters as needed.
          </Typography>

          <div className="panel-body">
            <div className="panel-body-content">
              <div className="filters-section">
                <Typography
                  variant="subtitle1"
                  className="filter-category-title"
                >
                  Care Plan Status
                </Typography>

                <Box position="relative">
                  <MultiSelectDropdown
                    label="Care Plan Status"
                    options={transformOptions(filterData.waitListStatus)}
                    selected={selectedFilters.carePlanstatus || []}
                    onApply={handleCarePlanChange}
                    placeholder="Select a status"
                    disabled={isLoading}
                  />
                  {isLoading && (
                    <Box
                      position="absolute"
                      top="50%"
                      right={40}
                      style={{ transform: 'translateY(-50%)', zIndex: 1 }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </Box>
              </div>
              <div className="filters-section">
                <Typography
                  variant="subtitle1"
                  className="filter-category-title"
                >
                  Waitlist Reason
                </Typography>

                <Box position="relative">
                  <MultiSelectDropdown
                    label="Waitlist Reason"
                    options={transformOptions(filterData.waitListReasons)}
                    selected={selectedFilters.isWaitlistReason || []}
                    onApply={handleWaitlistReasonChange}
                    placeholder="Select a Reason"
                    disabled={isLoading}
                  />
                  {isLoading && (
                    <Box
                      position="absolute"
                      top="50%"
                      right={40}
                      style={{ transform: 'translateY(-50%)', zIndex: 1 }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </Box>
              </div>
              <div className="filters-section">
                <Typography
                  variant="subtitle1"
                  className="filter-category-title"
                >
                  Jumpstarter
                </Typography>

                <RadioGroup
                  aria-label="jumpstarter"
                  name="jumpstarter"
                  value={
                    selectedFilters.isJumpstarter === true
                      ? 'yes'
                      : selectedFilters.isJumpstarter === false
                      ? 'no'
                      : ''
                  }
                  onChange={handleJumpstarterChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio style={{ fill: '#6f42f5', color: '#6f42f5' }} />
                    }
                    label="Yes"
                    disabled={isLoading}
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio style={{ fill: '#6f42f5', color: '#6f42f5' }} />
                    }
                    label="No"
                    disabled={isLoading}
                  />
                </RadioGroup>
              </div>
              <div className="filters-section">
                <MUIRangeSlider
                  min={0}
                  max={40}
                  value={
                    selectedFilters.recommendedHoursRange ||
                    RECOMMENDED_HOURS_RANGE
                  }
                  onChange={handleRecommendedHoursChange}
                  step={1}
                  disabled={isLoading}
                  label="Select Recommended Hours"
                  showValueLabels={true}
                />
              </div>
              <div className="filters-section">
                <Typography
                  variant="subtitle1"
                  className="filter-category-title"
                >
                  VBC Only ?
                </Typography>

                <RadioGroup
                  aria-label="vbc-only"
                  name="vbc-only"
                  value={
                    selectedFilters.isVBC === true
                      ? 'yes'
                      : selectedFilters.isVBC === false
                      ? 'no'
                      : ''
                  }
                  onChange={handleVbcOnlyChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio style={{ fill: '#6f42f5', color: '#6f42f5' }} />
                    }
                    label="Yes"
                    disabled={isLoading}
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio style={{ fill: '#6f42f5', color: '#6f42f5' }} />
                    }
                    label="No"
                    disabled={isLoading}
                  />
                </RadioGroup>
              </div>

              {selectedFilters.isVBC === true && (
                <>
                  <div className="filters-section">
                    <Typography
                      variant="subtitle1"
                      className="filter-category-title"
                    >
                      Insurance Category
                    </Typography>

                    <Box position="relative">
                      <MultiSelectDropdown
                        label="Insurance Category"
                        options={transformOptions(
                          filterData.insuranceCategories
                        )}
                        selected={getSelectedValues(
                          selectedFilters.checkedInsuranceCategories || [],
                          filterData.insuranceCategories || []
                        )}
                        onApply={handleInsuranceCategoryChange}
                        placeholder="Select a Category"
                        disabled={isLoading}
                      />
                      {isLoading && (
                        <Box
                          position="absolute"
                          top="50%"
                          right={40}
                          style={{ transform: 'translateY(-50%)', zIndex: 1 }}
                        >
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </Box>
                  </div>
                  <div className="filters-section">
                    <Typography
                      variant="subtitle1"
                      className="filter-category-title"
                    >
                      Insurance Payer
                    </Typography>

                    <MultiSelectDropdown
                      label="Insurance Payer"
                      options={transformOptions(filterData.insurancePayors)}
                      selected={getSelectedValues(
                        selectedFilters.checkedInsurancePayors || [],
                        filterData.insurancePayors || []
                      )}
                      onApply={handleInsurancePayerChange}
                      placeholder="Select a Payer"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="filters-section">
                    <Typography
                      variant="subtitle1"
                      className="filter-category-title"
                    >
                      Insurance Plan
                    </Typography>

                    <MultiSelectDropdown
                      label="Insurance Plan"
                      options={transformOptions(filterData.insurancePlans)}
                      selected={getSelectedValues(
                        selectedFilters.checkedInsurancePlans || [],
                        filterData.insurancePlans || []
                      )}
                      onApply={handleInsurancePlanChange}
                      placeholder="Select a Plan"
                      disabled={isLoading}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="panel-footer">
            <div className="filter-buttons">
              <Button
                variant="outlined"
                color="primary"
                className={
                  !isLoading
                    ? 'clear-filters-button'
                    : 'clear-filters-button-loading'
                }
                onClick={handleClearFilters}
                disabled={
                  isLoading || disabledClearFilters || !hasAdditionalFilters()
                }
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AdditionalFilters;
